import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import ChooseChain from "@/pages/ChooseChain";
import Tokenomics from "@/pages/Tokenomics";
import RobbedWall from "@/pages/RobbedWall";
import MemeGenerator from "@/pages/MemeGenerator";
import Vault from "@/pages/Vault";
import OfficialLinks from "@/pages/OfficialLinks";
import Roadmap from "@/pages/Roadmap";
import RobbedRemix from "@/pages/RobbedRemix";
import Battles from "@/pages/Battles";
import BattleDetail from "@/pages/BattleDetail";
import HallOfFame from "@/pages/HallOfFame";
import Leaderboard from "@/pages/Leaderboard";
import PublicProfile from "@/pages/PublicProfile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/NotFound";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import MyContent from "@/pages/MyContent";
import Rewards from "@/pages/Rewards";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import { ASSETS } from "@/lib/assets";

function Loading() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-ink">
      <img src={ASSETS.shrugging} alt="loading" className="w-28 animate-floaty" />
      <p className="font-display text-neon mt-4 uppercase">Checking if you got robbed...</p>
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return; // let hash-anchor links (e.g. /settings#wallets) scroll themselves
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/chains" element={<ChooseChain />} />
        <Route path="/tokenomics" element={<Tokenomics />} />
        <Route path="/wall" element={<RobbedWall />} />
        <Route path="/memes" element={<MemeGenerator />} />
        <Route path="/vault" element={<Vault />} />
        <Route path="/links" element={<OfficialLinks />} />
        <Route path="/roadmap" element={<Roadmap />} />
        <Route path="/remix" element={<RobbedRemix />} />
        <Route path="/battles" element={<Battles />} />
        <Route path="/battles/:id" element={<BattleDetail />} />
        <Route path="/hall-of-fame" element={<HallOfFame />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/u/:userId" element={<PublicProfile />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/settings" element={<Protected><Settings /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/rewards" element={<Protected><Rewards /></Protected>} />
        <Route path="/my/memes" element={<Protected><MyContent mode="memes" /></Protected>} />
        <Route path="/my/stories" element={<Protected><MyContent mode="stories" /></Protected>} />
        <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="bottom-center" theme="dark" toastOptions={{
          style: { background: "#1A1A1A", border: "3px solid #000", color: "#F5F5F0", fontFamily: "JetBrains Mono, monospace" },
        }} />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
